import type { ReactNode } from 'react'
import { LumoPaw } from './LumoPaw'
import { initialsOf } from '../lib/format'

export interface SummaryRow {
  label: string
  value: ReactNode
  pending?: boolean
}

interface BookingShellProps {
  children: ReactNode
  /** 0–100, drives the thin bar above the form. */
  progress: number
  practitionerName?: string
  practitionerCredentials?: string
  practitionerImageUrl?: string
  organizationName?: string
  headline?: string
  note?: string
  summaryRows?: SummaryRow[]
  statusPill?: { label: string; status: 'confirmed' | 'cancelled' }
  onBack?: () => void
  backLabel?: string
}

/**
 * Two-pane layout mirroring the Portal sign-up screen: the interactive step on
 * the left, a live summary of what the guest has chosen on the right.
 */
export function BookingShell({
  children,
  progress,
  practitionerName,
  practitionerCredentials,
  practitionerImageUrl,
  organizationName,
  headline,
  note,
  summaryRows = [],
  statusPill,
  onBack,
  backLabel = 'Back',
}: BookingShellProps) {
  const boundedProgress = Math.max(4, Math.min(100, progress))

  return (
    <main className="booking-shell">
      <section className="booking-main" aria-label="Book an appointment">
        <div className="booking-main-inner">
          <div className="booking-progress" aria-hidden="true">
            <span style={{ transform: `scaleX(${boundedProgress / 100})` }} />
          </div>

          <div className="booking-brand">
            <LumoPaw title="" aria-hidden />
            <span>Lumo</span>
          </div>

          {onBack && (
            <button type="button" className="booking-back" onClick={onBack}>
              <span aria-hidden="true">{'<'}</span>
              {backLabel}
            </button>
          )}

          {children}
        </div>
      </section>

      <aside className="booking-aside">
        <div className="booking-card">
          {statusPill && (
            <span className="booking-status-pill" data-status={statusPill.status}>
              {statusPill.label}
            </span>
          )}

          {practitionerName && (
            <div className="booking-practitioner">
              <span className="booking-avatar">
                {practitionerImageUrl
                  ? <img src={practitionerImageUrl} alt="" />
                  : initialsOf(practitionerName)}
              </span>
              <span className="booking-practitioner-copy">
                <strong>{practitionerName}</strong>
                <small>
                  {[practitionerCredentials, organizationName].filter(Boolean).join(' · ')}
                </small>
              </span>
            </div>
          )}

          {summaryRows.length > 0 && (
            <dl className="booking-summary-rows">
              {summaryRows.map((row) => (
                <div className="booking-summary-row" key={row.label}>
                  <dt>{row.label}</dt>
                  <dd className={row.pending ? 'is-pending' : undefined}>{row.value}</dd>
                </div>
              ))}
            </dl>
          )}

          {headline && <p className="booking-summary-headline">{headline}</p>}
          {note && <p className="booking-summary-note">{note}</p>}
        </div>
      </aside>
    </main>
  )
}

export function CenteredState({
  title,
  description,
  loading,
  children,
}: {
  title: string
  description?: string
  loading?: boolean
  children?: ReactNode
}) {
  return (
    <div className="booking-centered">
      <div className="booking-centered-inner">
        {loading
          ? <div className="booking-spinner" role="status" aria-label="Loading" />
          : (
            <div className="booking-brand" style={{ justifyContent: 'center' }}>
              <LumoPaw title="" aria-hidden />
              <span>Lumo</span>
            </div>
          )}
        <h1 className="booking-title">{title}</h1>
        {description && <p className="booking-description">{description}</p>}
        {children}
      </div>
    </div>
  )
}
